import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchApplicationDocumentBlob } from '../api/client';
import ApplicationPrint from './ApplicationPrint';
import { renderPdfPages } from '../utils/pdfPages';
import { APPLICATION_LABELS, DOC_LABELS, formatSlotTime } from '../utils/serviceApplications';

const statusClass = (status) => {
  const map = {
    pending: 'status-pending',
    contacted: 'status-contacted',
    scheduled: 'status-scheduled',
    completed: 'status-completed',
    cancelled: 'status-cancelled',
  };
  return map[status] || 'status-pending';
};

const DocPreview = ({ applicationId, doc }) => {
  const [url, setUrl] = useState('');
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    let objectUrl = '';
    fetchApplicationDocumentBlob(applicationId, doc.id)
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [applicationId, doc.id]);

  const isPdf = (doc.content_type || '').includes('pdf') || (doc.original_name || '').toLowerCase().endsWith('.pdf');
  const label = DOC_LABELS[doc.doc_type] || doc.original_name;

  return (
    <article className="doc-preview">
      <h3>{label}</h3>
      {failed ? <p className="dash-muted">Preview unavailable.</p> : null}
      {!failed && !url ? <p className="dash-muted">Loading preview…</p> : null}
      {url && !isPdf ? <img src={url} alt={label} /> : null}
      {url && isPdf ? <iframe title={label} src={url} /> : null}
      {url ? (
        <a href={url} download={doc.original_name}>
          Download
        </a>
      ) : null}
    </article>
  );
};

const isPdfDoc = (doc) =>
  (doc.content_type || '').includes('pdf') || (doc.original_name || '').toLowerCase().endsWith('.pdf');

const ApplicationDetail = ({ applications, isAdmin, onStatusChange, onSchedule, onDelete }) => {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const [printPages, setPrintPages] = useState(null);
  const [preparingPrint, setPreparingPrint] = useState(false);
  const [printError, setPrintError] = useState('');
  const objectUrls = useRef([]);

  const releasePrint = useCallback(() => {
    objectUrls.current.forEach((url) => URL.revokeObjectURL(url));
    objectUrls.current = [];
    setPrintPages(null);
  }, []);

  useEffect(() => releasePrint, [releasePrint]);

  const handlePrint = async (application) => {
    setPreparingPrint(true);
    setPrintError('');
    try {
      const pages = [];
      for (const doc of application.documents || []) {
        const label = DOC_LABELS[doc.doc_type] || doc.original_name;
        const blob = await fetchApplicationDocumentBlob(application.id, doc.id);
        if (isPdfDoc(doc)) {
          const images = await renderPdfPages(blob);
          images.forEach((src, i) =>
            pages.push({ key: `${doc.id}-${i}`, label, src, page: i + 1, total: images.length }),
          );
        } else {
          const src = URL.createObjectURL(blob);
          objectUrls.current.push(src);
          pages.push({ key: String(doc.id), label, src, page: 1, total: 1 });
        }
      }
      setPrintPages(pages);
    } catch (err) {
      setPrintError('Could not load every document for printing. Please try again.');
    } finally {
      setPreparingPrint(false);
    }
  };
  const row = applications.find((item) => String(item.id) === String(applicationId));

  if (!row) {
    return (
      <section className="dash-panel application-detail">
        <p>This application is not in the current list.</p>
        <button type="button" className="dash-btn-reset-filters" onClick={() => navigate('/dashboard/applications')}>
          Back to applications
        </button>
      </section>
    );
  }

  const isTerminal = row.status === 'completed' || row.status === 'cancelled';
  // Every type can get an appointment (same as the ops hub); "scheduled" opens the schedule form.
  const statusOptions = ['pending', 'contacted', 'scheduled', 'completed', 'cancelled'];
  const hasAppointment = Boolean(row.scheduled_date && row.scheduled_time);
  const visitDate = row.scheduled_date || row.preferred_date;
  const visitTime = row.scheduled_time || row.preferred_time;

  return (
    <section className="dash-panel application-detail">
      <div className="application-detail-head">
        <button type="button" className="dash-btn-reset-filters" onClick={() => navigate('/dashboard/applications')}>
          <i className="fas fa-arrow-left" /> Applications
        </button>
        <div>
          <h2>
            {APPLICATION_LABELS[row.application_type] || 'Application'} #{row.id}
          </h2>
          <p className="dash-muted">{row.customer_name}</p>
        </div>
        <div className="application-detail-actions">
          <button type="button" className="dash-btn-reset-filters" onClick={() => handlePrint(row)} disabled={preparingPrint}>
            <i className="fas fa-print" /> {preparingPrint ? 'Preparing…' : 'Print application'}
          </button>
          <span className={`dash-status-pill ${statusClass(row.status)}`}>{row.status}</span>
        </div>
      </div>
      {printError ? <p className="dash-muted">{printError}</p> : null}
      {printPages ? <ApplicationPrint row={row} docPages={printPages} onDone={releasePrint} /> : null}

      <div className="application-detail-grid">
        <div className="detail-card">
          <h3>Customer</h3>
          <p>{row.customer_name}</p>
          <p>{row.phone}</p>
          {row.email ? <p>{row.email}</p> : null}
          {row.city ? <p>{row.city}</p> : null}
        </div>
        <div className="detail-card">
          <h3>Vehicle</h3>
          <p>{row.vehicle_name || '—'}</p>
        </div>
        <div className="detail-card">
          <h3>Visit</h3>
          <p>
            {visitDate || 'No date'}
            {visitTime ? ` at ${formatSlotTime(visitTime) || visitTime}` : ''}
          </p>
          {isAdmin && !isTerminal ? (
            <button type="button" className="detail-card-link" onClick={() => onSchedule(row)}>
              {hasAppointment ? 'Reschedule' : 'Set date'}
            </button>
          ) : null}
        </div>
      </div>

      {row.notes ? (
        <div className="application-detail-notes">
          <h3>Request</h3>
          <p className="dash-notes">{row.notes}</p>
        </div>
      ) : null}

      <div className="application-detail-docs">
        <h3>Documents</h3>
        {(row.documents || []).length === 0 ? (
          <p className="dash-muted">No documents were uploaded.</p>
        ) : (
          <div className="doc-preview-grid">
            {row.documents.map((doc) => (
              <DocPreview key={doc.id} applicationId={row.id} doc={doc} />
            ))}
          </div>
        )}
      </div>

      {isAdmin ? (
        <div className="application-detail-controls">
          <h3>Status</h3>
          {isTerminal ? (
            <p className="dash-muted">This application is {row.status} and can no longer be changed.</p>
          ) : (
            <div className="status-actions">
              {statusOptions.map((status) => (
                <button
                  key={status}
                  type="button"
                  className={row.status === status ? 'active' : ''}
                  onClick={() => onStatusChange(row.id, status, row)}
                >
                  {status}
                </button>
              ))}
            </div>
          )}
          {!isTerminal ? (
            <button type="button" className="btn-table-schedule" onClick={() => onSchedule(row)}>
              <i className="fas fa-calendar-plus" />{' '}
              {row.status === 'scheduled' || hasAppointment ? 'Reschedule appointment' : 'Schedule appointment'}
            </button>
          ) : null}
          {onDelete ? (
            <button type="button" className="dash-delete-btn" onClick={() => onDelete(row)}>
              <i className="fas fa-trash" /> Delete application
            </button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
};

export default ApplicationDetail;
